import { useCallback, useState, useEffect } from 'react';
import { Alert } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { OrderCancelRequestDto } from '@tiendanube/common';
import { ResolvedInvoiceStatusDto } from '@tiendanube/common/src/domains/orders/commons';
import { Domain as DomainEnum, Reason } from '@tiendanube/common/src/enums';
import {
  Text,
  Select,
  Checkbox,
  InterfaceNameValue,
  InterfaceNameChecked,
  Stack,
  Modal,
} from '@tiendanube/components';
import FeatureFlag from 'App/components/FeatureFlag';
import ModalAside from 'App/components/lab/ModalAside';
import { INVOICE_ENABLED } from 'App/featuresFlags';
import { CancelAndSaveButtons, Input } from 'commons/components';
import { useModal } from 'commons/hooks';
import useOrderCancel, {
  DEFAULT_CANCEL_VALUES,
  CUSTOM_REASON_MAX_LENGTH,
} from 'commons/hooks/useOrderCancel';
import { InterfaceSetToggle } from 'commons/useToggle';
import { isNuvemPagoProvider } from 'commons/utils';
import useOrderInvoices from 'domains/Orders/Orders/hooks/useOrderInvoices';
import useRefundOrder from 'domains/Orders/Orders/hooks/useRefundOrder';
import CheckboxCancelInvoice from '../OrderDetails/CheckboxCancelInvoice';

const cancelOrder = {
  reasons: [
    Reason.CUSTOMER,
    Reason.INVENTORY,
    Reason.FRAUD,
    Reason.TEST,
    Reason.OTHER,
  ],
  defaultValues: DEFAULT_CANCEL_VALUES,
};

interface SingleOrderCancelModalProps {
  orderId: number;
  canBeRefunded: boolean;
  amount: number;
  gateway: string;
  isPaid: boolean;
  show: boolean;
  hideModal: InterfaceSetToggle;
  appliedAction: (cancelOrder: OrderCancelRequestDto) => void;
  isLoading: boolean;
  fulfillmentOrderId: string | undefined;
  orderIdentification: string;
  onError?: () => void;
}

function SingleOrderCancelModal({
  orderId,
  canBeRefunded,
  amount,
  gateway,
  show,
  hideModal,
  appliedAction,
  isPaid,
  isLoading,
  fulfillmentOrderId,
  orderIdentification,
  onError,
}: SingleOrderCancelModalProps): JSX.Element {
  const { t } = useTranslation([DomainEnum.ORDERS]);
  const { isLoading: isLoadingHasFunds, fetchHasAvailableFunds } =
    useRefundOrder(amount, gateway);
  const [showFundsWarning, openFundsWarning, closeFundsWarning] = useModal();
  const [showAlertRefundNuvemPago, setShowAlertRefundNuvemPago] =
    useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [invoiceStatus, setInvoiceStatus] =
    useState<ResolvedInvoiceStatusDto | null>(null);

  const handleInvoiceError = useCallback(() => {
    onError?.();
  }, [onError]);

  const { fetchResolvedInvoices } = useOrderInvoices({
    orderId: orderIdentification,
    fulfillmentOrderId: fulfillmentOrderId || '',
    operationReason: '',
    onInvoiceError: handleInvoiceError,
    onInvoiceOperationError: handleInvoiceError,
  });

  const {
    cancelOrderState,
    handleSelectChange,
    handleCheckboxChange,
    handleCustomReasonChange,
    isValidToCancel,
    selectRef,
    textareaRef,
  } = useOrderCancel({
    defaultValues: cancelOrder.defaultValues,
    isOpen: show,
  });

  const [shouldCancelInvoice, setShouldCancelInvoice] = useState(false);

  const [cancelInvoiceFn, setCancelInvoiceFn] = useState<
    (() => Promise<boolean>) | null
  >(null);

  const handleOnReady = useCallback(({ canCancelInvoice, cancelInvoice }) => {
    setShouldCancelInvoice(canCancelInvoice);
    setCancelInvoiceFn(() => cancelInvoice);
  }, []);

  useEffect(() => {
    if (fulfillmentOrderId) {
      fetchResolvedInvoices(setInvoiceStatus);
    }
  }, [fulfillmentOrderId, fetchResolvedInvoices]);

  const hasInternalInvoices =
    invoiceStatus?.invoices?.some((invoice) => !invoice.is_external) || false;

  const handleChangeSelect = ({ name, value }: InterfaceNameValue) => {
    const nameSanitizate = name.replace('single_', '');
    handleSelectChange({ name: nameSanitizate, value });
  };

  const handleChangeCheckbox = ({ name, checked }: InterfaceNameChecked) => {
    const nameSanitizate = name.replace('single_', '');
    handleCheckboxChange({ name: nameSanitizate, checked });
  };

  const handleChangeCheckboxRefund = ({
    name,
    checked,
  }: InterfaceNameChecked) => {
    setShowAlertRefundNuvemPago(checked);
    const nameSanitizate = name.replace('single_', '');
    handleCheckboxChange({ name: nameSanitizate, checked });
  };

  const onClickPrimary = async () => {
    const isNuvemPago = isNuvemPagoProvider(gateway);
    if (cancelOrderState.refund && isNuvemPago) {
      const hasFunds = await fetchHasAvailableFunds();
      return hasFunds ? onConfirm() : openFundsWarning();
    }

    return onConfirm();
  };

  const onConfirm = async () => {
    setIsSaving(true);
    closeFundsWarning();
    if (shouldCancelInvoice && cancelInvoiceFn) {
      const success = await cancelInvoiceFn();
      if (!success) {
        setIsSaving(false);
        onError?.();
        return;
      }
    }
    await appliedAction(cancelOrderState);
    setIsSaving(false);
    hideModal();
  };

  const options = cancelOrder.reasons.map((option) => ({
    label: t(`cancelModal.reason.options.${option}`),
    value: option,
  }));

  return (
    <>
      <ModalAside
        isOpen={show}
        onDidDismiss={hideModal}
        headerContent={t('cancelModal.titleDetail', { orderId })}
      >
        <Stack column align="stretch">
          {isPaid && !canBeRefunded && (
            <Alert
              appearance="warning"
              title={t('cancelModal.alertPaidTitle')}
              show
            >
              {t('cancelModal.alertPaidDescription')}
            </Alert>
          )}
          <div ref={selectRef}>
            <Select
              label={t('cancelModal.reason.label')}
              name="single_reason"
              options={options}
              value={cancelOrderState.reason}
              onChange={handleChangeSelect}
            />
          </div>
          {cancelOrderState.reason === Reason.OTHER && (
            <div ref={textareaRef}>
              <Input
                type="textarea"
                label={t('cancelModal.customReason.label')}
                placeholder={t('cancelModal.customReason.placeholder')}
                name="customReason"
                value={cancelOrderState.customReason}
                onChange={handleCustomReasonChange}
                maxLength={CUSTOM_REASON_MAX_LENGTH}
                helpText={t('cancelModal.customReason.counter', {
                  current: cancelOrderState.customReason?.length || 0,
                  max: CUSTOM_REASON_MAX_LENGTH,
                })}
                helpTextColor="neutral-textDisabled"
                lines={3}
              />
            </div>
          )}
          <Checkbox
            label={t('cancelModal.email')}
            name="single_email"
            onChange={handleChangeCheckbox}
            checked={cancelOrderState.email}
          />
          <Checkbox
            label={t('cancelModal.restock')}
            name="single_restock"
            onChange={handleChangeCheckbox}
            checked={cancelOrderState.restock}
          />
          {canBeRefunded && (
            <Checkbox
              label={t('cancelModal.refund')}
              name="single_refund"
              onChange={handleChangeCheckboxRefund}
              checked={cancelOrderState.refund}
            />
          )}
          {!!fulfillmentOrderId && hasInternalInvoices && (
            <FeatureFlag flag={INVOICE_ENABLED}>
              <CheckboxCancelInvoice
                label={t('cancelModal.invoice')}
                orderId={orderIdentification}
                fulfillmentOrderId={fulfillmentOrderId}
                cancelReason={t('cancelModal.cancelReasonInvoice')}
                onReady={handleOnReady}
              />
            </FeatureFlag>
          )}

          {showAlertRefundNuvemPago && (
            <Alert appearance="neutral">{t('cancelModal.alertRefund')}</Alert>
          )}

          <CancelAndSaveButtons
            saveText={t('cancelModal.actionPrimary')}
            cancelText={t('cancelModal.actionSecondary')}
            onCancel={hideModal}
            onSave={onClickPrimary}
            isDisabled={isSaving || isLoading || !isValidToCancel}
            isLoading={isSaving || isLoading || isLoadingHasFunds}
          />
        </Stack>
      </ModalAside>

      <Modal
        title={t('cancelModal.fundsWarningModal.title', { orderId })}
        show={showFundsWarning}
        onDismiss={closeFundsWarning}
        primaryAction={{
          children: `${t('cancelModal.fundsWarningModal.actionPrimary')}`,
          onClick: onConfirm,
          appearance: 'primary',
        }}
        secondaryAction={{
          children: `${t('cancelModal.fundsWarningModal.actionSecondary')}`,
          onClick: closeFundsWarning,
        }}
      >
        <Text bold>{t('cancelModal.fundsWarningModal.subtitle')}</Text>
        <Text>{t('cancelModal.fundsWarningModal.info')}</Text>
      </Modal>
    </>
  );
}

export default SingleOrderCancelModal;
